import { useCallback, useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useForm } from '@hooks/useDirtyForm';
import { useAppSelector } from '@src/store/hooks';
import { selectSelects } from '@src/store/slices/selects/selects';
import { selectCoverageArea } from '@store/slices/coverageArea/coverageAreaSlice';
import { selectHomePageModalSearch } from '@store/slices/search/homeSearchSlice';
import {
  useLazyGetContractorsAtmsSelectDataQuery,
  useLazyGetContractorsLosQuery,
  useLazyGetContractorsTypeSelectDataQuery,
} from '@store/rtk/selects';
import { useLazyGetStatesQuery } from '@store/rtk/coverageArea';
import { useSearchContractorsMutation } from '@store/rtk/contractors';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { encrypt } from '@src/utils';
import { idType, selectItemType } from '@src/types';
import { selectUsers } from '@store/slices/users/usersSlice';

export type ContractorSearchFormValues = {
  contractorName: string;
  tin: string;
  state: selectItemType | null;
  dispatchPlatform: selectItemType | null;
  los: selectItemType[] | null;
  contractorType: selectItemType[] | null;
};

const SEARCH_RIDE_PAGE_SIZE = 20;

const DEFAULT_FORM_VALUES: ContractorSearchFormValues = {
  contractorName: '',
  tin: '',
  state: null,
  dispatchPlatform: null,
  los: null,
  contractorType: null,
};

function buildFilters(
  values: Partial<ContractorSearchFormValues>,
  includeTaxId: boolean
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(values)) {
    if (key === 'tin' && !includeTaxId) continue;
    if (value === null || value === undefined || value === '') continue;

    if (Array.isArray(value)) {
      const ids = value.map((item) => item?.id).filter(Boolean);
      if (ids.length > 0) result[`${key}Id`] = ids;
    } else if (typeof value === 'object') {
      if ((value as selectItemType).id) {
        result[`${key}Id`] = (value as selectItemType).id;
      }
    } else {
      result[key] = value;
    }
  }

  return result;
}

export const useContractorSearch = () => {
  const prevFiltersRef = useRef<string>('');

  const [searchContractors, { isLoading: isContractorLoading }] =
    useSearchContractorsMutation();

  const [getContractorsLos] = useLazyGetContractorsLosQuery();
  const [getContractorsTypeSelectData] =
    useLazyGetContractorsTypeSelectDataQuery();
  const [getContractorsAtmsSelectData] =
    useLazyGetContractorsAtmsSelectDataQuery();
  const [getStates] = useLazyGetStatesQuery();

  const { states } = useAppSelector(selectCoverageArea);
  const { contractorsType, contractorsAtms, contractorsLos } =
    useAppSelector(selectSelects);
  const { searchContractorData } = useAppSelector(selectHomePageModalSearch);
  const { isContractorVersion: isAuxConEnabled } = useAppSelector(selectUsers);

  const { control } = useForm<ContractorSearchFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });

  const watchedFormValues = useWatch<ContractorSearchFormValues>({ control });
  const debouncedValues = useDebouncedValue<ContractorSearchFormValues>(
    watchedFormValues as ContractorSearchFormValues,
    500
  );

  const [filters, setFilters] = useState<Record<string, unknown>>({});

  useEffect(() => {
    const cleanedValues = buildFilters(debouncedValues, isAuxConEnabled);

    const serialized = JSON.stringify(cleanedValues);
    if (serialized === prevFiltersRef.current) return;

    prevFiltersRef.current = serialized;
    setFilters(cleanedValues);

    searchContractors({
      argument: { body: cleanedValues },
      params: { size: SEARCH_RIDE_PAGE_SIZE },
    });
  }, [debouncedValues, isAuxConEnabled, searchContractors]);

  const handleClickEdit = useCallback((id: idType) => {
    const encodeId = encrypt({ contractor: id });
    window.open(`/contractors/information/${encodeId}`, '_blank');
  }, []);

  const loadLos = useCallback(() => getContractorsLos({}), [getContractorsLos]);
  const loadContractorTypes = useCallback(
    () => getContractorsTypeSelectData({}),
    [getContractorsTypeSelectData]
  );
  const loadAtms = useCallback(
    () => getContractorsAtmsSelectData({}),
    [getContractorsAtmsSelectData]
  );
  const loadStates = useCallback(() => getStates({}), [getStates]);

  return {
    control,
    states,
    contractorsType,
    contractorsAtms,
    contractorsLos,
    loadLos,
    loadContractorTypes,
    loadAtms,
    loadStates,
    searchContractorData,
    isContractorLoading,
    isAuxConEnabled,
    filters,
    handleClickEdit,
  };
};
